import type { SocialReportProfile } from "contracts/api/social/report";
import { SOCIAL_JUDGMENTS } from "contracts/enums/social";
import {
  SOCIAL_PLATFORM_LABELS,
  SOCIAL_STANDARD_LABELS,
} from "contracts/enums/social-labels";
import { For, Show } from "solid-js";
import {
  EXTRACTION_PHASE_BY_STATUS,
  REPORT_EXTRACTION_STATUS_LABELS,
  REPORT_JUDGMENT_LABELS,
  SOCIAL_EXTRACTION_PHASE,
} from "../../enums/social-report";
import { pluralize } from "../../ui/format";
import { ChecksLine } from "./SocialReportSummary";
import { profileHeadline } from "./socialReportModel";

/** One registered profile: identity, extraction state, and its rollup. */
export function SocialReportProfileCard(props: {
  profile: SocialReportProfile;
}) {
  const extraction = () => props.profile.latestExtraction;
  const phase = () => {
    const latest = extraction();
    return latest ? EXTRACTION_PHASE_BY_STATUS[latest.status] : null;
  };
  const headline = () => profileHeadline(props.profile);

  return (
    <header class="social-report-profile-head">
      <div class="social-report-profile-identity">
        <strong>
          {SOCIAL_PLATFORM_LABELS[props.profile.platform]} · @
          {props.profile.handle}
        </strong>
        <Show when={props.profile.displayName} keyed>
          {(name) => <span class="td-muted">{name}</span>}
        </Show>
        <a href={props.profile.profileUrl} target="_blank" rel="noreferrer">
          View profile
        </a>
      </div>
      <ExtractionState extraction={extraction()} phase={phase()} />
      <ProfileHeadlineLine headline={headline()} />
      <Show when={headline().kind === "scored"}>
        <StandardsBreakdown profile={props.profile} />
      </Show>
    </header>
  );
}

/** Per-standard verdict mix, shown only once something has been analysed. */
function StandardsBreakdown(props: { profile: SocialReportProfile }) {
  return (
    <dl class="social-report-standards">
      <For each={props.profile.rollup.standards}>
        {(entry) => (
          <div class="social-report-standard">
            <dt>{SOCIAL_STANDARD_LABELS[entry.standard]}</dt>
            <dd>
              <For
                each={SOCIAL_JUDGMENTS.filter(
                  (judgment) => entry.verdicts[judgment] > 0,
                )}
              >
                {(judgment) => (
                  <span class="social-report-verdict" data-judgment={judgment}>
                    {entry.verdicts[judgment]}{" "}
                    {REPORT_JUDGMENT_LABELS[judgment]}
                  </span>
                )}
              </For>
            </dd>
          </div>
        )}
      </For>
    </dl>
  );
}

function ExtractionState(props: {
  extraction: SocialReportProfile["latestExtraction"];
  phase: ReturnType<typeof phaseOf>;
}) {
  const extraction = props.extraction;
  if (!extraction) {
    return (
      <p class="social-report-extraction" data-phase="none">
        No posts have been collected from this profile yet.
      </p>
    );
  }
  return (
    <p class="social-report-extraction" data-phase={props.phase ?? "none"}>
      <span class="social-state">
        {REPORT_EXTRACTION_STATUS_LABELS[extraction.status]}
      </span>
      <Show when={props.phase === SOCIAL_EXTRACTION_PHASE.IN_PROGRESS}>
        <span class="td-muted">
          {" "}
          — {extraction.acceptedPosts}{" "}
          {pluralize(extraction.acceptedPosts, "post", "posts")} collected so
          far
        </span>
      </Show>
      <Show
        when={
          props.phase === SOCIAL_EXTRACTION_PHASE.FAILED && extraction.error
        }
        keyed
      >
        {(error) => <span class="gate-error"> {error}</span>}
      </Show>
      <Show when={props.phase === SOCIAL_EXTRACTION_PHASE.ATTENTION}>
        <span class="td-muted">
          {" "}
          — the last collection could not confirm it started; our team is on it.
        </span>
      </Show>
    </p>
  );
}

function phaseOf(profile: SocialReportProfile) {
  const latest = profile.latestExtraction;
  return latest ? EXTRACTION_PHASE_BY_STATUS[latest.status] : null;
}

function ProfileHeadlineLine(props: {
  headline: ReturnType<typeof profileHeadline>;
}) {
  const headline = props.headline;
  if (headline.kind === "no-posts") {
    return (
      <p class="social-report-headline td-muted">
        Nothing to assess yet — no posts have been captured.
      </p>
    );
  }
  if (headline.kind === "awaiting-analysis") {
    return (
      <p class="social-report-headline td-muted">
        {headline.capturedPosts}{" "}
        {pluralize(headline.capturedPosts, "post", "posts")} captured; analysis
        has not concluded yet.
      </p>
    );
  }
  return (
    <div class="social-report-headline">
      <span class="td-muted">
        {headline.analysedPosts} of {headline.capturedPosts}{" "}
        {pluralize(headline.capturedPosts, "captured post", "captured posts")}{" "}
        analysed.
      </span>
      <ChecksLine checks={headline.checks} />
    </div>
  );
}
